import { Add } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import { createProjectVenue } from '@/domain/project-venue/actions/create-project-venue.action';
import { ProjectVenue } from '@/domain/project-venue/entities/project-venue.entity';
import {
  checkUserCreateVenue,
  selectIsCreatingProjectVenue,
} from '@/domain/project-venue/project-venue.selectors';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSSecondaryButton } from '@/ui/components/button/FSSecondaryButton';
import { FSModal } from '@/ui/components/modal/FSModal';
import { FSModalTitle } from '@/ui/components/modal/FSModalTitle';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';

import { ProjectVenueForm } from '../../components/ProjectVenueForm';

const initialProjectState: ProjectVenue = {
  id: -1,
  type: 'venue',
  image: '',
  name: '',
  startDate: '',
  endDate: '',
  maxCapacity: NaN,
  location: '',
  models: {
    // TODO could we remove these data here (models) and other field that cannot be updated by the form
    structure: { ess: 'ESS_SEASON', env: 'ENV_SEASON' },
    events: { ess: 'ESS_SEASON_EVENT', env: 'ENV_SEASON_EVENT' },
  },
  properties: {
    presetTitle: '',
    avgCapacity: 0,
    accommodation: [],
    equipment: [],
    mobility: [],
  },
};

export function ButtonModalCreateProjectVenue() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isCreatingProject = useAppSelector(selectIsCreatingProjectVenue);
  const userCanCreateVenue = useAppSelector(checkUserCreateVenue);

  const [open, setOpen] = useState(false);
  const [project, setProject] = useState<ProjectVenue>(initialProjectState);

  const onClose = () => {
    setOpen(false);
    reset();
  };

  const onSubmit = async (data: ProjectVenue) => {
    try {
      const params = { project: data };
      await dispatch(createProjectVenue(params)).unwrap();
      reset();
      setOpen(false);
      navigate('/app/projects/venue');
    } catch (error) {
      console.error(error);
    }
  };

  const reset = () => {
    setProject({ ...initialProjectState });
  };

  const handleOpen = () => {
    if (userCanCreateVenue) {
      setOpen(true);
    } else {
      enqueueSnackbar(translate('License.MaxEvent'), {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        action: (
          <FSSecondaryButton onClick={() => navigate('/app/contact')}>
            {translate('License.MaxEventAction')}
          </FSSecondaryButton>
        ),
      });
    }
  };

  return (
    <>
      <FSPrimaryButton startIcon={<Add />} onClick={handleOpen}>
        {translate('ProjectsVenue.new-project-button')}
      </FSPrimaryButton>
      <FSModal open={open} onClose={onClose}>
        <Stack spacing={3}>
          <FSModalTitle title={translate('ProjectsVenue.create-your-venue')} />
          <ProjectVenueForm
            project={project}
            onSubmit={onSubmit}
            buttons={
              <FormButtons
                isCreatingProject={isCreatingProject}
                onClose={onClose}
              />
            }
          />
        </Stack>
      </FSModal>
    </>
  );
}

function FormButtons({
  isCreatingProject,
  onClose,
}: {
  isCreatingProject: boolean;
  onClose: () => void;
}) {
  return (
    <Stack
      display={'flex'}
      direction={'row'}
      justifyContent={'end'}
      spacing={1}
      sx={{ mt: 2 }}
    >
      <FSSecondaryButton onClick={onClose}>
        {translate('Form.cancel')}
      </FSSecondaryButton>
      <FSPrimaryButton disabled={isCreatingProject} type="submit">
        {translate('ProjectsVenue.Form.create-season')}
      </FSPrimaryButton>
    </Stack>
  );
}
